import { type ComponentType, lazy } from "react";

import { currentRelease } from "./currentRelease.ts";
import { reloadWithFreshShell } from "./reloadWithFreshShell.ts";

const RETRY_DELAYS_MS = [150, 400];
const MAX_RELOADS_PER_WINDOW = 2;
const RELOAD_WINDOW_MS = 5 * 60 * 1000;

/**
 * Wrapper around React.lazy that handles chunks the server no longer has
 * (typically a stale shell after a deployment): retry the import first, and if
 * it stays broken, reload onto the new build, rate-limited per build.
 */
export function lazyWithRetry<T extends ComponentType>(
  componentImport: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await importWithRetries(componentImport);
    } catch (error) {
      const recentReloads = recentReloadsOfThisBuild();
      if (!isReloadWorthwhile(recentReloads)) throw error;

      sessionStorage.setItem(
        reloadKey(),
        [...recentReloads, Date.now()].join(","),
      );
      void reloadWithFreshShell();

      // Never resolve: the page is about to reload
      return new Promise<{ default: T }>(() => {});
    }
  });
}

// Retrying heals only a transient network failure, and only where the browser lets a failed module be re-fetched; the reload above is the guaranteed path
export async function importWithRetries<T>(load: () => Promise<T>): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await load();
    } catch (error) {
      if (attempt >= RETRY_DELAYS_MS.length || !navigator.onLine) throw error;
      await delay(RETRY_DELAYS_MS[attempt]);
    }
  }
}

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Any failure counts: the error type for a missing chunk depends on bundler and browser, so it is deliberately not classified
function isReloadWorthwhile(recentReloads: number[]) {
  return navigator.onLine && recentReloads.length < MAX_RELOADS_PER_WINDOW;
}

function recentReloadsOfThisBuild(): number[] {
  const stored = sessionStorage.getItem(reloadKey());
  if (!stored) return [];

  const now = Date.now();
  return stored
    .split(",")
    .map(Number)
    .filter((reloadedAt) => now - reloadedAt < RELOAD_WINDOW_MS);
}

function reloadKey() {
  return `chunk-failed-reload:${currentRelease()}`;
}
